import { useEffect, useState } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import { listWords } from '@verlo/shared/lib/allergens';
import { hitWords } from '../../lib/allergyCheck';

/**
 * The send check: a dish this table can't have is about to go to the kitchen.
 *
 * Only when there is a clash: a round with nothing in it the table can't have sends exactly
 * as it always did. The server checks with the guest and either takes the dish off, or sends
 * it with a note saying what the kitchen should change. The note is required: "it clashes"
 * is no use to a chef, "without cheese and sour cream" is.
 *
 * A dish that lists a word the till can't read stops here too (`hit.check`): the till can't
 * say it clashes and can't say it doesn't, which is exactly when somebody should ask.
 *
 * `hits` is `clashingLines(…)`. `onSend(notesByIndex)` and `onRemove(indexes)` are keyed by
 * each line's place in the cart.
 */
const AllergyClashDialog = ({ open, onOpenChange, hits, tableName, tableAllergens = [], allergyNotes, onSend, onRemove }) => {
  const [notes, setNotes] = useState({});
  useEffect(() => { if (open) setNotes({}); }, [open]);

  const one = hits.length === 1;
  const allClashes = [...new Set(hits.flatMap((h) => h.clash))];
  const title = one
    ? `${hits[0].line.name} ${hitWords(hits[0])}`
    : allClashes.length
      ? `${hits.length} dishes contain ${listWords(allClashes)}${hits.some((h) => h.check?.length) ? ', or might' : ''}`
      : `${hits.length} dishes list allergens the till can't check`;
  const complete = hits.every((h) => (notes[h.index] || '').trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="confirm" className="border-t-4 border-t-signal-stop-line" data-testid="allergy-clash-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {tableName} has {listWords(tableAllergens)} on its allergies
            {allergyNotes ? ` (${allergyNotes})` : ''}. Check with the guest before it goes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {hits.map((h) => (
            <label key={h.index} className="block border border-hairline bg-white px-3 py-2.5">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">
                {one ? 'For the kitchen' : `${h.line.name} ${hitWords(h)} · for the kitchen`}
              </span>
              <Input
                className="mt-1"
                autoFocus={h === hits[0]}
                placeholder="What should the kitchen change?"
                value={notes[h.index] || ''}
                onChange={(e) => setNotes((prev) => ({ ...prev, [h.index]: e.target.value }))}
                data-testid={`allergy-clash-note-${h.index}`}
              />
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onRemove(hits.map((h) => h.index))} data-testid="allergy-clash-remove">
            {one ? 'Take it off' : 'Take them off'}
          </Button>
          <Button
            onClick={() => onSend(notes)}
            disabled={!complete}
            className="bg-signal-stop hover:bg-signal-stop"
            data-testid="allergy-clash-send"
          >
            {one ? 'Send, the kitchen will adapt it' : 'Send, the kitchen will adapt them'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AllergyClashDialog;
